import type { Nullable } from '~/types'
import type { UseAuthentication } from '~/types/rental-api-aliases'

/**
 * Returns a collection of basic user details, advanced properties, and
 * helpers.
 *
 * @param user The user to get the details for.
 * @returns The user details.
 */
export function getUser(user?: Nullable<UseAuthentication>) {
  // Base properties.
  const userAbout = user?.About ?? ''
  const userAvatar = user?.Avatar ?? ''
  const userBirthday = user?.Birthday ?? ''
  const userBusinessName = user?.BusinessName ?? ''
  const userCompletedPayment = user?.CompletedPayment ?? false
  const userCompletedPayout = user?.CompletedPayout ?? false
  const userCountry = user?.Country ?? ''
  const userCountryPhoneCode = formatCallingCode(user?.CountryPhoneCode ?? '')
  const userCountryPhoneCodeSecondary = formatCallingCode(user?.CountryPhoneCodeSecondary ?? '')
  const userCountryString = user?.CountryString ?? ''
  const userCreatedDate = user?.CreatedDate ?? ''
  const userCredit = user?.Credit ?? []
  const userDebtAmount = user?.DebtAmount ?? 0
  const userDebtReason = user?.DebtReason ?? ''
  const userEmail = user?.Email ?? ''
  const userFirstName = user?.FirstName ?? ''
  const userGender = user?.Gender ?? ''
  const userHasRv = user?.HasRv ?? false
  const userHasSmsConsent = user?.HasSmsConsent ?? false
  const userHasStripeRequirements = user?.HasStripeRequirements ?? false
  const userHearAboutUsAnswer = user?.HearAboutUsAnswer ?? ''
  const userHostStatisticsCriteriaMet = user?.HostStatisticsCriteriaMet ?? false
  const userId = user?.Id ?? 0
  const userInteractionFeatures = user?.Interactions?.Features ?? []
  const userIsInstantBookEligible = user?.IsInstantBookEligible ?? false
  const userIsOwner = user?.IsOwner ?? false
  const userIsSubscribeOwnerNewsletter = user?.IsSubscribeOwnerNewsletter ?? false
  const userIsSubscribeRenterNewsletter = user?.IsSubscribeRenterNewsletter ?? false
  const userLanguage = user?.Language ?? 'English'
  const userLastLogin = user?.LastLogin ?? ''
  const userLastLoginCity = user?.LastLoginCity ?? ''
  const userLastLoginCountry = user?.LastLoginCountry ?? ''
  const userLastLoginDevice = user?.LastLoginDevice ?? ''
  const userLastLoginIP = user?.LastLoginIP ?? ''
  const userLastLoginOs = user?.LastLoginOs ?? ''
  const userLastLoginRegion = user?.LastLoginRegion ?? ''
  const userLastLoginSoftware = user?.LastLoginSoftware ?? ''
  const userLastLoginUserAgent = user?.LastLoginUserAgent ?? ''
  const userLastName = user?.LastName ?? ''
  const userLastPayoutGuaranteeUpsellEmailSent = user?.LastPayoutGuaranteeUpsellEmailSent ?? ''
  const userLegalFirstName = user?.LegalFirstName ?? ''
  const userLegalLastName = user?.LegalLastName ?? ''
  const userLocation = user?.Location
  const userMethodOfPayment = user?.MethodOfPayment ?? ''
  const userNotes = user?.Notes ?? ''
  const userNumberOfVisit = user?.NumberOfVisit ?? 0
  const userPhoneNumber = user?.PhoneNumber ?? ''
  const userPhoneNumberSecondary = user?.PhoneNumberSecondary ?? ''
  const userProfileState = user?.ProfileState ?? undefined
  const userSignupAs = user?.SignupAs ?? ''
  const userSignupCountry = user?.SignupCountry ?? ''
  const userSignupFrom = user?.SignupFrom ?? ''
  const userStripeAccStatus = user?.StripeAccStatus ?? ''
  const userSuperHost = user?.SuperHost ?? {
    Id: 0,
    IsForced: false,
    IsActive: false,
    ProfileId: 0,
    AcquiredDate: '',
    LastAssessmentDate: '',
    CreditTicketSentDate: '',
    ResponseRate: 0,
    AcceptanceRate: 0,
    Cancellations: 0,
    UnansweredEnquiries: 0,
    ConfirmedOrCompletedBookingsPastYear: 0,
  }
  const userTimeZone = user?.TimeZone ?? ''
  const userTotalAvailableCredit = user?.TotalAvailableCredit ?? 0
  const userTotalPendingCredit = user?.TotalPendingCredit ?? 0
  const userUserType = user?.UserType ?? ''
  const userVerificationStatus = user?.VerificationStatus ?? 'NotStarted'

  // Advanced properties.
  const userFormattedPhoneNumber = userPhoneNumber ? `${userCountryPhoneCode} ${userPhoneNumber}` : ''
  const userFormattedPhoneNumberSecondary = userPhoneNumberSecondary ? `${userCountryPhoneCodeSecondary} ${userPhoneNumberSecondary}` : ''
  const userFullName = formatNameAsFullName(userFirstName, userLastName)
  const userInitials = formatNameAsInitials(userFirstName, userLastName)
  const userLocale = userLanguage === 'French' ? 'fr' : 'en'

  // Basic `is` checks.
  const userIsCanadian = userCountry === 'CA'
  const userIsSuperhost = userSuperHost?.IsActive
  const userIsVerificationStatusInProgress = userVerificationStatus === 'InProgress'
  const userIsVerificationStatusNotStarted = userVerificationStatus === 'NotStarted'
  const userIsVerified = userVerificationStatus === 'Verified'

  // Basic `has` checks.
  const userHasAvailableCredit = userTotalAvailableCredit > 0
  const userHasPhoneNumber = userPhoneNumber !== ''

  return {
    userAbout,
    userAvatar,
    userBirthday,
    userBusinessName,
    userCompletedPayment,
    userCompletedPayout,
    userCountry,
    userCountryPhoneCode,
    userCountryPhoneCodeSecondary,
    userCountryString,
    userCreatedDate,
    userCredit,
    userDebtAmount,
    userDebtReason,
    userEmail,
    userFirstName,
    userGender,
    userHasRv,
    userHasSmsConsent,
    userHasStripeRequirements,
    userHearAboutUsAnswer,
    userHostStatisticsCriteriaMet,
    userId,
    userInteractionFeatures,
    userIsInstantBookEligible,
    userIsOwner,
    userIsSubscribeOwnerNewsletter,
    userIsSubscribeRenterNewsletter,
    userLanguage,
    userLastLogin,
    userLastLoginCity,
    userLastLoginCountry,
    userLastLoginDevice,
    userLastLoginIP,
    userLastLoginOs,
    userLastLoginRegion,
    userLastLoginSoftware,
    userLastLoginUserAgent,
    userLastName,
    userLastPayoutGuaranteeUpsellEmailSent,
    userLegalFirstName,
    userLegalLastName,
    userLocale,
    userLocation,
    userMethodOfPayment,
    userNotes,
    userNumberOfVisit,
    userPhoneNumber,
    userPhoneNumberSecondary,
    userProfileState,
    userSignupAs,
    userSignupCountry,
    userSignupFrom,
    userStripeAccStatus,
    userSuperHost,
    userTimeZone,
    userTotalAvailableCredit,
    userTotalPendingCredit,
    userUserType,
    userVerificationStatus,

    userFormattedPhoneNumber,
    userFormattedPhoneNumberSecondary,
    userFullName,
    userInitials,

    userIsCanadian,
    userIsSuperhost,
    userIsVerificationStatusInProgress,
    userIsVerificationStatusNotStarted,
    userIsVerified,

    userHasAvailableCredit,
    userHasPhoneNumber,
  }
}
